<template>
    <v-app
        class="app-container"
        :class="{
            darkbg: ['tenantLogin','tenantRegister','userLogin','gettingStarted'].includes($route.name)
        }"
    >
        <!-- Top Progress Bar -->
        <v-progress-linear
            v-if="loading"
            class="loading"
            color="#1565c0"
            indeterminate
            absolute
        />

        <!-- Snackbar -->
        <cod-snackbar />

        <!-- Confirm Dialog -->
        <cod-confirm />

        <!-- Handle Subscription / Contract Expired -->
        <contract v-if="tenant && tenant.venue && ['me', 'sr', 'ba'].includes(tenant.venue.locale)" />
        <subscription v-if="tenant && tenant.venue && !['me', 'sr', 'ba'].includes(tenant.venue.locale)" />

        <!-- Notification -->
        <notification />

        <!-- OfflineTooLong -->
        <offline-too-long />

        <!-- Connection Management -->
        <connection-management />

        <!-- Page Tour Guide -->
        <cod-tour />

        <!-- Syncing with server overlay -->
        <v-overlay :value="syncStatus" z-index="999999">
            <div class="text-body-1">Syncing with the server</div>
            <v-progress-linear
                class="mt-3"
                color="accent"
                indeterminate
                rounded
                height="6"
            />
        </v-overlay>

        <!-- App Content -->
        <router-transition>
            <router-view />
        </router-transition>

    </v-app>
</template>

<script>
import OfflineMixin from '@/mixins/offline';
import WebsocketsMixin from '@/mixins/websockets';
import Device from '@/wrappers/Device';
import ErrorReporter from '@/wrappers/ErrorReporter';

import RouterTransition from '@/router/RouterTransition';
import CodSnackbar from '@/components/CodSnackbar';
import CodConfirm from '@/components/CodConfirm';
import CodTour from '@/components/CodTour';
import Subscription from '@/components/Subscription';
import Contract from '@/components/Contract';
import Notification from '@/components/Notification';
import OfflineTooLong from '@/components/OfflineTooLong';
import ConnectionManagement from '@/views/shared/ConnectionManagement';
import FiscalService from '@/services/FiscalService';
import AppEvent from '@/models/AppEvent';
import Shift from '@/models/Shift';
import LocalNetworkService from '@/services/LocalNetworkService';
import { AriaCapacitor } from '@codeus/aria-capacitor';
import { App } from '@capacitor/app';

import * as mutationTypes from '@/store/mutationTypes';
import KitchenTicket from './models/KitchenTicket';
const packageJson = require('/package.json');

export default {
    components: {
        RouterTransition,
        CodSnackbar,
        CodConfirm,
        CodTour,
        Subscription,
        Contract,
        Notification,
        OfflineTooLong,
        ConnectionManagement
    },

    mixins: [OfflineMixin, WebsocketsMixin],

    async created() {
        document.addEventListener(
            'swUpdated', this.onAppUpdate, { once: true }
        );

        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (this.refreshing) return

                this.refreshing = true
                window.location.reload()
            });
        }

        this.eventBus.$on('sync-status', syncStatus => {
            this.syncStatus = syncStatus;

            if(this.syncStatus === 'failed') {
                this.$snackbar(this.$t('general.error_contact_support'), 'error', { timeout: 5000 });
                this.syncStatus = null
            }
        });

        this.eventBus.$on('show-loading', loading => {
            this.loading = loading;
        });

        this.eventBus.$on('app-initialized', async () => {
            if(this.device.platform === 'android') {
                const osVersion = parseInt(this.device?.osVersion, 10)

                let permissions = await AriaCapacitor.checkPermissions()
                let permissionsGranted = permissions.esdc === 'granted'

                if(osVersion >= 12) {
                    permissionsGranted = permissionsGranted && permissions.bluetooth === 'granted'
                }

                if(!permissionsGranted) {
                    permissions = await AriaCapacitor.requestPermissions()
                }
            }
        });

        document.getElementById('app-loading').style.display = 'none'

        // Request persistent storage for site
        if (navigator.storage && navigator.storage.persist) {
            await navigator.storage.persist();
        }

        if(window.electron) {
            try {
                window.electron.on('el-print-server-print-remote', this.debounce(() => {
                    this.eventBus.$emit('orders-refresh');
                }, 3000));

                const electronData = await window.electron.invoke('el-info')
                if(electronData) {
                    Device.set({ ...this.device, electron: electronData })
                }
            } catch (error) {
                console.log(error.message)
            }
        }

        AriaCapacitor.addListener('cardReadCompleted', data => {
            this.eventBus.$emit('card-read-completed', data)
        });

        AppEvent.registerDefaultCallback(event => {
            if(!this.isOnline) return

            if(event.type === 'shiftUpdate') {
                if(!Shift.current()) this.$snackbar(this.$t('general.shift_closed'));
                else this.$snackbar(this.$t('general.shift_opened'), 'success');
            }

            if(event.type === 'orderUpdate' || event.type === 'orderDelete' || event.type === 'ordersSync') {
                this.eventBus.$emit('orders-updated')
            }

            if(event.type === 'kitchenTicketsSync') {
                this.eventBus.$emit('kitchen-tickets-sync')
            }

            const notification = { type: event.type, data: event.data };
            this.eventBus.$emit('notification-received', notification);
        })

        App.addListener('resume', () => {
            if(!Device.isMobile()) return

            if(!LocalNetworkService.connectionStatus?.connected) LocalNetworkService.reload()
        })
    },

    data: () => {
        return {
            refreshing: false,

            syncStatus: null,
            loading: false,
        };
    },

    methods: {
        onAppUpdate(event) {
            const registration = event.detail;

            const installNewApp = () => {
                this.$store.commit(mutationTypes.SET_UPDATE_PENDING, { updatePending: false });
                if (!registration || !registration.waiting) return

                registration.waiting.postMessage('skipWaiting');
                console.log(`App ${packageJson.version} updated`);
            }

            if(this.$store.getters.updatePending || process.env.VUE_APP_IMMEDIATE_APP_UPDATES === 'true') {
                installNewApp()
                return
            }

            this.$store.commit(mutationTypes.SET_UPDATE_PENDING, { updatePending: true });

            this.$snackbar(this.$t('update.update_available_message'), 'success', {
                timeout: 0,
                buttonText: this.$t('update.update_now'),
                y: 'top',
                mode: 'multi-line',
                clickHandler: installNewApp,
            })
        },

        debounce(func, wait) {
            let timeout;

            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };

                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        }
    }
};
</script>

<style lang="scss">

// Utility
@import './styles/utility/typography';
@import './styles/utility/spaces';
@import './styles/utility/blocks';
@import './styles/utility/buttons';
@import './styles/utility/other';
@import './styles/utility/vuetify-custom';
@import './styles/ckeditor';

html {
    height: 100%;
    overflow: visible;
}

body {
    height: 100%;
    margin: 0;
    overflow: auto;
}

.app-container {
    &.theme--light.v-application {
        background: #e4e6ee;

        &.darkbg {
            background-color: #0f1219 !important;
        }
    }

    /* Disable text selection, or your app will feel like a web page */
    -webkit-user-select: none;
    -webkit-app-region: drag;

    .install-app-alert {
        z-index: 1;
        position: absolute;
        top: 0px;
        width: 100%;
        margin: 0;
    }

    .loading {
        z-index: 999999;
    }
}

hr {
    border-style: solid;
    border-width: 1px 0 0 0;
    border-color: #e9eef6;
    height: 2px;
}

.hide-spinners {
    input[type='number']::-webkit-inner-spin-button,
    input[type='number']::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
}
</style>
